import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { type CountryCode, type ProjectCode } from '@constants'
import { useOrderAnalyticsEvent } from '@dni-analysis'
import { ORDER_PLATFORM } from '@dni-analysis/types/order-events'
import { useDialog } from '@dni-hooks/useDialog'
import { useOrder } from '@hooks-api/ec/use-order'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { FastpassOrderStatus } from '@hooks-api/types/dataModel/fpOrder'
import { OrderDetailProvider, type OrderDetailProviderProps } from '@order-detail/components/shared/OrderDetailProvider/OrderDetailProvider'
import { DIALOG_CONFIRM_CANCEL_TRANSACTION_KEY, type DialogConfirmCancelTransactionProps } from '@order-detail/components/shared/Orders/DialogConfirmCancelTransaction'
import { OrderDetail } from '@order-detail/components/shared/Orders/OrderDetail'
import { SkeletonOrderDetail } from '@order-detail/components/shared/Orders/SkeletonOrderDetail'
import { Box } from '@pets-ui/Box/Box'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { LayoutMyAccount } from '@wp-my-account/components/LayoutMyAccount'
import { useHistory, useParams } from 'react-router-dom-v5'
import { TheMerchantHeader } from '../../components/TheMerchantHeader'
import { useFastpassMenuItems } from '../../hooks/use-fastpass-menu-items'

export function OrderDetailContainer({ projectCode, countryCode, campaignId, environment }: { projectCode: ProjectCode, countryCode: CountryCode, campaignId: number, environment?: OrderDetailProviderProps['environment'] }) {
  const { transactionId } = useParams<{ transactionId: string }>()
  const { order: { destructuredData: transaction, isLoading: isLoadingOrder } } = useOrder({ transactionId, countryCode })
  const { campaignCodeFromUrl } = useCampaignCode()
  const history = useHistory()
  const { destructuredData: campaignMember } = useApiCampaignMember({ code: campaignCodeFromUrl })
  const menuItem = useFastpassMenuItems('merchant', campaignCodeFromUrl, campaignMember?.user_name)
  const { openDialog: openConfirmCancelTransactionDialog } = useDialog<DialogConfirmCancelTransactionProps>(DIALOG_CONFIRM_CANCEL_TRANSACTION_KEY)
  const { sendOrderCancelSuccessEvent } = useOrderAnalyticsEvent({ userId: campaignMember?.platform_uid ?? '', platform: ORDER_PLATFORM.TO_RESELLER })
  const canCancelTransaction = transaction?.status === FastpassOrderStatus.PENDING
  const canPaymentTransaction = transaction?.status === FastpassOrderStatus.PENDING

  const retryPaymentUrl = () => {
    history.push(`/merchant/${campaignCodeFromUrl}/retry-payment/${transactionId}`)
  }

  const handleCancelTransaction = () => {
    if (!transactionId) {
      console.error('[OrderSummary] no transactionId')
      return
    }
    const orderId = transaction?.child_orders?.[0]?.order_id ?? ''
    openConfirmCancelTransactionDialog({
      transactionId,
      environment: 'merchant',
      onCancelSuccess: () => sendOrderCancelSuccessEvent({ orderId, transactionId }),
    })
  }

  const countryCodeFromOrder = transaction?.country_code
  const isWarehouseOrSubscription = !!transaction?.child_orders[0]?.subship_info?.type
  const orderDetailContextValue: OrderDetailProviderProps = {
    transaction,
    currency: transaction?.currency,
    showRecipientInfo: true,
    countryCode: countryCodeFromOrder,
    onPayment: canPaymentTransaction ? retryPaymentUrl : undefined,
    onCancel: canCancelTransaction ? handleCancelTransaction : undefined,
    showPdf: transaction?.need_pdf,
    isWarehouseOrSubscription,
    environment,
  }

  return (
    <>
      <OrderDetailProvider {...orderDetailContextValue}>
        <TheMerchantHeader
          projectCode={projectCode}
          countryCode={countryCode}
          campaignId={campaignId}
        />
        <Box backgroundColor={colors.grey100}>
          <LayoutMyAccount menuItem={menuItem}>
            {isLoadingOrder
              ? <SkeletonOrderDetail />
              : <OrderDetail />}
          </LayoutMyAccount>
        </Box>
      </OrderDetailProvider>
    </>
  )
}
